import { useState, useEffect, useCallback, useRef } from 'react';
import {
  useCreateInventoryRecordMutation,
  useCreateInventoryRecordWithItemsMutation,
} from '../../../../redux/services/inventoryApi';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../../redux/slices/auth.slice';
import { ProductsService, LocationsService, InventoryRecordsService } from '../../../../api';
import { ProductsServiceHelpers } from '../../../../utils/api-helpers/ProductsService.helpers';
import {
  CreateInventoryRecordWithItemsDto,
  InventoryRecordItemDto,
  IndividualWarehouseUnitDto,
} from '../../../../api';
import { ProductItem, LocationData, ProductData } from '../types';
import {
  isReductionVersusSavedUnits,
  filterProductUnitsToProductItems,
  trimProductUnitsToProductItems,
  mergeUnitsAdditive,
} from '../productUnitsMerge.utils';

interface AddInventoryPanelProps {
  open: boolean;
  onClose: () => void;
  /** Parent save must return a Promise so the panel can await and block double-submit. */
  onSave?: (data: any) => void | Promise<void>;
  editMode?: boolean;
  recordToEdit?: any;
}

export const useAddInventoryPanel = ({
  open,
  onClose,
  onSave,
  editMode = false,
  recordToEdit = null,
}: AddInventoryPanelProps) => {
  // Get the current user from Redux state
  const currentUser = useSelector(selectCurrentUser);

  // Replace the query hook with state variables
  const [locationsData, setLocationsData] = useState<LocationData[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState<any>(null);

  const [createInventoryRecord, { isLoading: isSubmitting, isSuccess, error: submitError }] =
    useCreateInventoryRecordMutation();
  const [
    createInventoryRecordWithItems,
    {
      isLoading: isSubmittingWithItems,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isSuccess: isSuccessWithItems,
      error: submitErrorWithItems,
    },
  ] = useCreateInventoryRecordWithItemsMutation();

  // State for products
  const [productsData, setProductsData] = useState<ProductData[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<any>(null);

  // Panel state
  const [currentStep, setCurrentStep] = useState(1);
  const [productItems, setProductItems] = useState<ProductItem[]>([
    { id: 1, productName: '', productCode: '', quantity: '' },
  ]);
  // This is actually a location ID from the backend
  const [selectedLocationId, setSelectedLocationId] = useState('');
  // Selected supplier ID
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [showSupplierValidation, setShowSupplierValidation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  // State for save draft confirmation modal
  const [showSaveDraftConfirmModal, setShowSaveDraftConfirmModal] = useState(false);

  // Block double-submit on confirm / draft (ref catches clicks before re-render)
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmingRef = useRef(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const savingDraftRef = useRef(false);

  // State for cancel confirmation (when clicking X)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Aviso: bajar cantidad o quitar productos pierde propiedades guardadas en el paso 2
  const [showReductionWarningModal, setShowReductionWarningModal] = useState(false);

  // State for error snackbar (draft validation/API errors)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Store timeout IDs to clear them when component unmounts or panel closes
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  // Track IDs already enriched with defaults to avoid duplicate fetches while panel is open
  const fetchedDefaultsIdsRef = useRef<Set<number>>(new Set());

  // Add state for product units with properties and costs
  const [productUnits, setProductUnits] = useState<any[]>([]);

  /** IDs de líneas de detalle al abrir edición: sirve para enviar `delete: true` si el usuario quita filas. */
  const editLoadedRecordItemIdsRef = useRef<Set<number>>(new Set());

  /** Tras el modal de reducción: 'next' = vaciar e ir al paso 2; 'saveDraft' = recortar unidades y luego confirmar borrador. */
  const reductionAfterActionRef = useRef<'next' | 'saveDraft' | null>(null);

  /**
   * Evita re-hidratar productItems/productUnits desde recordToEdit en cada cambio de productsData
   * (p. ej. fetchMissingProductDefaults), lo que borraba filas nuevas al editar un borrador.
   * Solo se marca cuando todos los ítems del registro ya se mapearon al catálogo local.
   */
  const editFormHydratedRecordIdRef = useRef<number | null>(null);

  // Create a fetch function for locations
  const fetchLocations = async (retryCount = 0) => {
    try {
      setLocationsLoading(true);
      // Use the LocationsService to fetch locations
      const response = await LocationsService.locationsControllerFindAll();

      // Process the response data
      let locationsList: LocationData[] = [];

      // eslint-disable-next-line no-console
      console.log('Locations response:', response);
      if (response && Array.isArray(response.data)) {
        locationsList = response.data
          .filter((item: any) => item.type === 'WAREHOUSE' && item.status === 'ACTIVE')
          .map((item: any) => ({
            id: item.id,
            name: item.name,
            type: item.type,
            status: item.status,
          }));
      }

      setLocationsData(locationsList);
      setLocationsError(null);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocationsError(error);

      // Retry up to 2 times with exponential backoff if the request fails
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000;
        // eslint-disable-next-line no-console
        console.log(`Retrying in ${delay}ms...`);
        setTimeout(() => {
          fetchLocations(retryCount + 1);
        }, delay);
      }
    } finally {
      setLocationsLoading(false);
    }
  };

  /**
   * Warm a small active-product cache for edit hydration; product selection itself is async/search-based.
   */
  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      // Keep this bounded. Missing selected products are hydrated by fetchMissingProductDefaults.
      const response = await ProductsService.productsControllerFindAll(
        1, // page
        20, // limit
        undefined, // searchName
        undefined, // searchCode
        undefined, // searchTerm
        undefined, // categoryId
        'ACTIVE', // status - only active products
        undefined, // supplierId
      );

      // Transform the response data into the format needed by the component
      let productsList: ProductData[] = [];

      if (response && Array.isArray(response.data)) {
        productsList = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          supplier: item.supplier,
          material: item.material,
          weightGrams: item.weightGrams,
          lengthMm: item.lengthMm,
          diameterSize: item.diameterSize,
          color: item.color,
          workshopCode: item.workshopCode,
          costPrice: item.costPrice,
          salePrice: item.salePrice,
        }));
      } else if (Array.isArray(response)) {
        productsList = response.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          supplier: item.supplier,
          material: item.material,
          weightGrams: item.weightGrams,
          lengthMm: item.lengthMm,
          diameterSize: item.diameterSize,
          color: item.color,
          workshopCode: item.workshopCode,
          costPrice: item.costPrice,
          salePrice: item.salePrice,
        }));
      }

      setProductsData(productsList);
      setProductsError(null);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductsError(error);
    } finally {
      setProductsLoading(false);
    }
  };

  // Reset form when panel opens
  useEffect(() => {
    if (open) {
      fetchLocations();
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the form after locations data is loaded (but not in edit mode)
  useEffect(() => {
    if (open && locationsData.length > 0 && !editMode) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationsData, editMode]);

  // Reset confirmation modal state when panel opens or closes
  useEffect(() => {
    // Clear all pending timeouts when panel closes or opens
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current = [];
    if (!open) {
      fetchedDefaultsIdsRef.current.clear();
      reductionAfterActionRef.current = null;
      editFormHydratedRecordIdRef.current = null;
    }

    // Reset confirmation modal state
    setShowConfirmModal(false);
    setConfirmMessage('');
    setShowCancelConfirmation(false);
    setShowReductionWarningModal(false);
  }, [open]);

  // Refresca el set de item ids del registro al abrir cierre/edicion (para deletions)
  useEffect(() => {
    if (!open) {
      editLoadedRecordItemIdsRef.current.clear();
      return;
    }
    if (editMode && recordToEdit?.items && Array.isArray(recordToEdit.items)) {
      const ids = (recordToEdit.items as { id?: number }[])
        .map((i) => i.id)
        .filter((id): id is number => id != null && !Number.isNaN(Number(id)) && Number(id) > 0);
      editLoadedRecordItemIdsRef.current = new Set(ids);
    }
  }, [open, editMode, recordToEdit]);

  // Initialize component when it first mounts
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('locations data setif', locationsData);
    if (locationsData && locationsData.length > 0) {
      // eslint-disable-next-line no-console
      console.log('Initial location setup with:', locationsData[0].id.toString());
      setSelectedLocationId(locationsData[0].id.toString());
    }
  }, [locationsData]);

  // Set default location when locations are loaded or when moving to step 2
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Locations:', locationsData);
    // eslint-disable-next-line no-console
    console.log('Selected location before setting:', selectedLocationId);
    // Set a default location if none is selected and locations are available
    if (
      locationsData &&
      locationsData.length > 0 &&
      (!selectedLocationId || selectedLocationId === '')
    ) {
      const defaultLocationId = locationsData[0].id.toString();
      // eslint-disable-next-line no-console
      console.log('Setting selected location to:', defaultLocationId);
      setSelectedLocationId(defaultLocationId);
    }
  }, [locationsData, selectedLocationId]);

  // Make sure a location is selected when on step 2
  useEffect(() => {
    if (
      currentStep === 2 &&
      locationsData.length > 0 &&
      (!selectedLocationId || selectedLocationId === '')
    ) {
      // eslint-disable-next-line no-console
      console.log('Setting location on step 2 activation');
      setSelectedLocationId(locationsData[0].id.toString());
    }
  }, [currentStep, locationsData, selectedLocationId]);

  // Set supplier ID when in edit mode (needs to run first to trigger products fetch)
  useEffect(() => {
    if (editMode && recordToEdit && open) {
      // Set the supplier ID first - this will trigger products fetch
      if (recordToEdit.supplier?.id || recordToEdit.supplier_id) {
        const supplierId = (recordToEdit.supplier?.id ?? recordToEdit.supplier_id).toString();
        setSelectedSupplierId(supplierId);
      } else {
        console.warn('No supplier found in record to edit');
        setSelectedSupplierId('');
      }

      // Set the branch/location ID (support both branch_id and destinationLocationId)
      const locationId =
        recordToEdit.branch_id ??
        recordToEdit.destinationLocationId ??
        recordToEdit.destinationLocation?.id;
      if (locationId != null) {
        setSelectedLocationId(locationId.toString());
      }

      // Siempre comenzar en el paso 1 al abrir un registro en edición
      setCurrentStep(1);
    }
  }, [editMode, recordToEdit, open]);

  // Load product items when in edit mode AND products are available
  useEffect(() => {
    if (!open) {
      return;
    }

    if (editMode && recordToEdit && productsData.length > 0) {
      const recordId = Number(recordToEdit.id);
      if (
        editFormHydratedRecordIdRef.current != null &&
        editFormHydratedRecordIdRef.current === recordId
      ) {
        return;
      }

      // eslint-disable-next-line no-console
      console.log('Loading record items for editing:', recordToEdit);

      // Check if record is PENDING
      const isPending = recordToEdit.status === 'PENDING' || recordToEdit.status === 'Pendiente';

      // Format and set product items
      if (recordToEdit.items && recordToEdit.items.length > 0) {
        // eslint-disable-next-line no-console
        console.log('Raw items from record:', recordToEdit.items);

        /** Orden de registro: misma noción que en API (id de línea ascendente). */
        const itemsInRegistrationOrder = [...recordToEdit.items].sort((a: any, b: any) => {
          const idA = a.id != null ? Number(a.id) : NaN;
          const idB = b.id != null ? Number(b.id) : NaN;
          if (Number.isNaN(idA) && Number.isNaN(idB)) return 0;
          if (Number.isNaN(idA)) return 1;
          if (Number.isNaN(idB)) return -1;
          return idA - idB;
        });

        const formattedItems = itemsInRegistrationOrder
          .map((item: any, index: number) => {
            // Extract the correct product ID from the item
            // Different APIs might return different formats, so we need to handle both
            const productId =
              item.productId || item.product_id || (item.product ? item.product.id : null);

            // Find the product name and code based on the product ID
            const product = productsData.find((p) => p.id === productId);

            // eslint-disable-next-line no-console
            console.log(`Mapping item ${index}:`, {
              item,
              productId,
              foundProduct: product,
            });

            return {
              id: index + 1,
              productId: productId,
              productName: product ? product.name : '',
              productCode: product ? product.code : '',
              quantity: item.quantity ? item.quantity.toString() : '0',
              // Keep original ID for API updates
              originalItemId: item.id,
            };
          })
          .filter((item: ProductItem) => item.productId && item.productName); // Filter out items without valid product info

        // eslint-disable-next-line no-console
        console.log('Formatted items for edit form:', formattedItems);

        if (formattedItems.length > 0) {
          setProductItems(formattedItems);
        } else {
          console.warn('No valid items could be mapped from the record data');
          setProductItems([{ id: 1, productName: '', productCode: '', quantity: '' }]);
        }

        const expectedItemCount = itemsInRegistrationOrder.length;
        const allRecordItemsMapped =
          expectedItemCount === 0 || formattedItems.length >= expectedItemCount;
        if (allRecordItemsMapped && !Number.isNaN(recordId) && recordId > 0) {
          editFormHydratedRecordIdRef.current = recordId;
        }

        // If PENDING record has individualUnits, load them into productUnits
        if (isPending) {
          const loadedUnits: any[] = [];

          itemsInRegistrationOrder.forEach((item: any) => {
            // eslint-disable-next-line no-console
            console.log('Processing item for individualUnits:', item);
            if (item.individualUnits && Array.isArray(item.individualUnits)) {
              // Get productId from item (could be productId or product.id)
              const productId =
                item.productId || item.product_id || (item.product ? item.product.id : null);
              const product = productsData.find((p) => p.id === productId);

              // Get product code from item.product.code or from productsData
              const productCode = item.product?.code || product?.code || '';
              const productName = item.product?.name || product?.name || '';

              // eslint-disable-next-line no-console
              console.log(
                `Loading ${item.individualUnits.length} individualUnits for product ${productCode}`,
                {
                  productId,
                  product,
                  individualUnits: item.individualUnits,
                },
              );

              item.individualUnits.forEach(
                (unit: IndividualWarehouseUnitDto, unitIndex: number) => {
                  loadedUnits.push({
                    id: `${productCode || 'product'}-${unitIndex + 1}`,
                    productId: productId,
                    productName: productName,
                    productCode: productCode,
                    quantity: 1,
                    properties: {
                      materialId: unit.materialId
                        ? unit.materialId.toString()
                        : product?.material?.id
                          ? product.material.id.toString()
                          : '',
                      materialName: product?.material?.name || '',
                      weight: unit.weightGrams
                        ? unit.weightGrams.toString()
                        : product?.weightGrams
                          ? product.weightGrams.toString()
                          : '',
                      length:
                        unit.lengthMm != null
                          ? (Number(unit.lengthMm) / 10).toString()
                          : product?.lengthMm != null
                            ? (Number(product.lengthMm) / 10).toString()
                            : '',
                      diameter:
                        unit.diameterSize != null && String(unit.diameterSize).trim() !== ''
                          ? String(unit.diameterSize)
                          : product?.diameterSize != null &&
                              String(product.diameterSize).trim() !== ''
                            ? String(product.diameterSize)
                            : '',
                      color: unit.color || product?.color || '',
                      workshopCode: unit.workshopCode || product?.workshopCode || '',
                    },
                    costs: {
                      costPrice: unit.costPrice
                        ? unit.costPrice.toString()
                        : product?.costPrice
                          ? product.costPrice.toString()
                          : '',
                      salePrice: unit.salePrice
                        ? unit.salePrice.toString()
                        : product?.salePrice
                          ? product.salePrice.toString()
                          : '',
                    },
                  });
                },
              );
            } else {
              console.warn('Item has no individualUnits or it is not an array:', item);
            }
          });

          if (loadedUnits.length > 0) {
            // eslint-disable-next-line no-console
            console.log('Loaded individualUnits from JSONB:', loadedUnits);
            setProductUnits(loadedUnits);
          } else {
            console.warn(
              'No individualUnits were loaded. Check if items have individualUnits property.',
            );
          }
        }
      }
    }
  }, [editMode, recordToEdit, open, productsData]);

  // If there's an error, show it
  useEffect(() => {
    if (submitError) {
      console.error('Error submitting inventory record:', submitError);
      setConfirmMessage('No se pudo crear el registro. Intente de nuevo.');
    }
  }, [submitError]);

  const resetForm = () => {
    setCurrentStep(1);

    // Only reset product items if not in edit mode or if we're closing the panel
    if (!editMode || !open) {
      setProductItems([{ id: 1, productName: '', productCode: '', quantity: '' }]);
      // Only reset productUnits when not in edit mode or closing
      setProductUnits([]);
    }

    // Only set the selectedLocation if locations are available, otherwise keep it as is
    if (locationsData && locationsData.length > 0 && (!editMode || !recordToEdit)) {
      setSelectedLocationId(locationsData[0].id.toString());
    }

    // Reset supplier selection if not in edit mode
    if (!editMode || !open) {
      setSelectedSupplierId('');
    }

    setShowConfirmModal(false);
    setConfirmMessage('');
    setShowSupplierValidation(false);
  };

  const handleSupplierChange = useCallback((supplierId: string) => {
    setSelectedSupplierId(supplierId);
    if (supplierId) {
      setShowSupplierValidation(false);
    }
  }, []);

  // Handler for adding product row
  const addProductRow = useCallback(() => {
    setProductItems((prevItems) => {
      const newId = prevItems.length > 0 ? Math.max(...prevItems.map((item) => item.id)) + 1 : 1;
      return [...prevItems, { id: newId, productName: '', productCode: '', quantity: '' }];
    });
  }, []);

  // Handler for removing product row
  const removeProductRow = useCallback((id: number) => {
    setProductItems((prevItems) => {
      if (prevItems.length > 1) {
        const newItems = prevItems.filter((item) => item.id !== id);
        setProductUnits((prevUnits) => filterProductUnitsToProductItems(prevUnits, newItems));
        return newItems;
      }
      return prevItems;
    });
  }, []);

  // Handler for updating product information
  // Uses functional setState to handle rapid sequential updates (e.g., when selecting a product)
  const updateProductItem = useCallback(
    (id: number, field: keyof ProductItem, value: string) => {
      setProductItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === id) {
            // If updating product name, do not resolve id/code by name (names are not unique).
            // Selection flow always follows with productCode + productId updates.
            if (field === 'productName') {
              return {
                ...item,
                productName: value,
              };
            }
            // If updating product code, also update the name and productId
            if (field === 'productCode' && value) {
              const selectedProduct = productsData.find((p: any) => p.code === value);
              return {
                ...item,
                [field]: value,
                // Preserve existing values when product isn't in the local page cache.
                productName: selectedProduct?.name ?? item.productName,
                productId: selectedProduct?.id ?? item.productId,
              };
            }
            // If updating productId directly (most reliable - always sets all fields)
            if (field === 'productId' && value) {
              const productId = parseInt(value, 10);
              const selectedProduct = productsData.find((p: any) => p.id === productId);
              return {
                ...item,
                productId,
                productName: selectedProduct?.name ?? item.productName,
                productCode: selectedProduct?.code ?? item.productCode,
              };
            }
            return { ...item, [field]: value };
          }
          return item;
        }),
      );
    },
    [productsData],
  );

  // Batch-fetch defaults for selected products that are not present in local productsData.
  // This keeps step 2 inheritance robust with large catalogs + async paginated search.
  const fetchMissingProductDefaults = useCallback(async (productIds: number[]) => {
    if (productIds.length === 0) {
      return;
    }

    try {
      const response = await ProductsServiceHelpers.getDefaultsBatch(productIds);
      const defaults = Array.isArray(response?.data) ? response.data : [];
      if (defaults.length === 0) {
        return;
      }

      setProductsData((prev) => {
        const byId = new Map(prev.map((p) => [p.id, p]));

        defaults.forEach((item) => {
          const existing = byId.get(item.id);
          byId.set(item.id, {
            id: item.id,
            name: item.name ?? existing?.name ?? '',
            code: item.code ?? existing?.code ?? '',
            supplier: existing?.supplier,
            material:
              item.materialId != null
                ? {
                    id: item.materialId,
                    name: item.materialName ?? '',
                  }
                : existing?.material,
            weightGrams:
              item.weightGrams != null ? String(item.weightGrams) : (existing?.weightGrams ?? ''),
            lengthMm:
              item.lengthMm != null
                ? String(Number(item.lengthMm) / 10)
                : (existing?.lengthMm ?? ''),
            diameterSize: item.diameterSize ?? existing?.diameterSize ?? '',
            color: item.color ?? existing?.color ?? '',
            workshopCode: item.workshopCode ?? existing?.workshopCode ?? '',
            costPrice:
              item.costPrice != null ? String(item.costPrice) : (existing?.costPrice ?? ''),
            salePrice:
              item.salePrice != null ? String(item.salePrice) : (existing?.salePrice ?? ''),
          });
        });

        return Array.from(byId.values());
      });
    } catch (error) {
      console.error('Error fetching product defaults in batch:', error);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedIds = Array.from(
      new Set(
        productItems
          .map((item) => {
            const n = Number(item.productId);
            return Number.isInteger(n) && n > 0 ? n : null;
          })
          .filter((id): id is number => id != null),
      ),
    );

    if (selectedIds.length === 0) {
      return;
    }

    const existingIds = new Set(productsData.map((product) => product.id));
    const missingIds = selectedIds.filter(
      (id) => !existingIds.has(id) && !fetchedDefaultsIdsRef.current.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => fetchedDefaultsIdsRef.current.add(id));
    void fetchMissingProductDefaults(missingIds);
  }, [open, productItems, productsData, fetchMissingProductDefaults]);

  // Validation functions wrapped in useCallback
  const validateStep1 = useCallback(() => {
    const hasValidProducts = productItems.every(
      (item) => item.productName && item.productCode && item.quantity && Number(item.quantity) > 0,
    );
    const hasSupplier = selectedSupplierId && selectedSupplierId !== '';
    return hasValidProducts && hasSupplier;
  }, [productItems, selectedSupplierId]);

  // Add validation for step 2 (product properties)
  const validateStep2 = useCallback(() => {
    // Basic validation - ensure all units have at least some properties filled
    if (productUnits.length === 0) return false;

    // Check if at least cost prices are filled (basic requirement)
    return productUnits.every(
      (unit) => unit.costs && unit.costs.costPrice && parseFloat(unit.costs.costPrice) > 0,
    );
  }, [productUnits]);

  // Handlers for step navigation (after validateStep1: used by handleNextStep)
  const handleNextStep = useCallback(() => {
    if (currentStep === 1) {
      if (!validateStep1()) {
        return;
      }
      if (productUnits.length > 0 && isReductionVersusSavedUnits(productItems, productUnits)) {
        reductionAfterActionRef.current = 'next';
        setShowReductionWarningModal(true);
        return;
      }
    }

    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setConfirmMessage('¿Confirmar registro?');
      setShowConfirmModal(true);
    }
  }, [currentStep, productItems, productUnits, validateStep1]);

  const handleConfirmReductionWarning = useCallback(() => {
    setShowReductionWarningModal(false);
    const action = reductionAfterActionRef.current;
    reductionAfterActionRef.current = null;
    if (action === 'saveDraft') {
      setProductUnits((prev) => {
        const stillInForm = filterProductUnitsToProductItems(prev, productItems);
        return trimProductUnitsToProductItems(productItems, stillInForm);
      });
      setShowSaveDraftConfirmModal(true);
      return;
    }
    // 'next' o legacy: continuar al paso 2 con unidades vacías para rehidratar
    setProductUnits([]);
    setCurrentStep(2);
  }, [productItems]);

  const handleCancelReductionWarning = useCallback(() => {
    reductionAfterActionRef.current = null;
    setShowReductionWarningModal(false);
  }, []);

  const handlePreviousStep = useCallback(() => {
    if (currentStep > 1) {
      // No vaciar productUnits aquí: si el usuario vuelve al paso 1 y luego avanza otra vez,
      // ProductPropertiesStep rehidrata desde initialProductUnits. Si en el paso 1 cambió
      // productos/cantidades, ProductPropertiesStep.hooks detecta snapshot desalineado y regenera.
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Add debug useEffect after validation functions are defined
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      'isNextDisabled:',
      (currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2()),
    );
  }, [currentStep, validateStep1, validateStep2]);

  // Handler for updating product properties
  const handleUpdateProperties = useCallback((units: any[]) => {
    setProductUnits(units);
  }, []);

  // Handler for updating product items
  const handleUpdateItem = useCallback((id: number, field: keyof ProductItem, value: string) => {
    setProductItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  }, []);

  // Helper function to extract numeric values from strings
  const extractNumericValue = (value: string, unit: string): number | undefined => {
    if (!value || value.trim() === '') return undefined;

    // Remove the unit and any non-numeric characters except decimal point
    const numericString = value.replace(new RegExp(unit, 'gi'), '').replace(/[^\d.]/g, '');
    const numericValue = parseFloat(numericString);

    return isNaN(numericValue) ? undefined : numericValue;
  };

  // Helper: align saved units with step-1 rows and fill missing units for new products.
  const buildUnitsPayloadForItems = useCallback(() => {
    const mergedUnits = mergeUnitsAdditive(
      productItems,
      productsData,
      filterProductUnitsToProductItems(productUnits, productItems),
      selectedSupplierId,
    );
    return trimProductUnitsToProductItems(productItems, mergedUnits);
  }, [productItems, productUnits, productsData, selectedSupplierId]);

  // Helper function to transform frontend unit data to backend format
  const transformUnitsToBackendFormat = (units: any[]): InventoryRecordItemDto[] => {
    const supplierIdNum = parseInt(selectedSupplierId, 10);
    const hasValidSupplierId = !isNaN(supplierIdNum) && supplierIdNum > 0;

    const normalizeProductId = (raw: unknown): number | undefined => {
      if (raw === undefined || raw === null || raw === '') return undefined;
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (Number.isNaN(n) || n <= 0) return undefined;
      return n;
    };

    // Group by stable catalog identity (productId). Never by display name.
    const unitsByProductId = new Map<number, any[]>();
    for (const unit of units) {
      const productId =
        normalizeProductId(unit.productId) ??
        normalizeProductId(productsData.find((p: any) => p.code === unit.productCode)?.id);

      if (productId == null) {
        throw new Error(
          `Product ID missing for unit (code=${unit.productCode ?? '?'}, name=${unit.productName ?? '?'})`,
        );
      }

      const list = unitsByProductId.get(productId) ?? [];
      list.push(unit);
      unitsByProductId.set(productId, list);
    }

    // Edit mode: map existing record item ids by productId (fallback: productCode)
    const productIdToItemId = new Map<number, number>();
    for (const item of productItems) {
      if (!item.originalItemId) continue;
      const pid = normalizeProductId(item.productId);
      if (pid != null) {
        productIdToItemId.set(pid, item.originalItemId);
        continue;
      }
      const byCode = productsData.find((p: any) => p.code === item.productCode);
      if (byCode?.id) {
        productIdToItemId.set(byCode.id, item.originalItemId);
      }
    }

    return Array.from(unitsByProductId.entries()).map(([productId, productUnits]) => {
      const typedProductUnits = productUnits as any[];

      const individualUnits: IndividualWarehouseUnitDto[] = typedProductUnits.map((unit: any) => {
        const individualUnit: IndividualWarehouseUnitDto = {
          supplierId: hasValidSupplierId ? supplierIdNum : undefined,
          weightGrams: extractNumericValue(unit.properties.weight, 'g'),
          lengthMm: (() => {
            const lengthCm = extractNumericValue(unit.properties.length, 'cm');
            return lengthCm != null ? lengthCm * 10 : undefined;
          })(),
          diameterSize: unit.properties.diameter || undefined,
          color: unit.properties.color || undefined,
          workshopCode: unit.properties.workshopCode || undefined,
          materialId: unit.properties.materialId ? parseInt(unit.properties.materialId) : undefined,
          costPrice: unit.costs.costPrice ? parseFloat(unit.costs.costPrice) : undefined,
          salePrice: unit.costs.salePrice ? parseFloat(unit.costs.salePrice) : undefined,
          condition: 'Excellent', // Default condition
          itemNotes: undefined, // Optional field
        };

        return individualUnit;
      });

      const itemId =
        editMode && productIdToItemId.has(productId) ? productIdToItemId.get(productId) : undefined;
      const item: InventoryRecordItemDto = {
        // Backend accepts optional id - use 0 for new items (backend treats falsy id as new item)
        id: itemId ?? 0,
        productId,
        quantity: typedProductUnits.length,
        individualUnits: individualUnits,
        delete: false,
      };

      return item;
    });
  };

  /** Resolve catalog productId for step-1 rows without relying on non-unique names. */
  const resolveProductIdFromItem = (item: {
    productId?: number | string;
    productCode?: string;
    productName?: string;
  }): number | null => {
    const raw = item.productId;
    if (raw !== undefined && raw !== null && raw !== '') {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    if (item.productCode) {
      const byCode = productsData.find((p: any) => p.code === item.productCode);
      if (byCode?.id) return byCode.id;
    }
    return null;
  };

  /** Map step-1 rows to API items; fail loudly if any row lacks a stable productId. */
  const mapProductItemsForSave = (): Array<{
    productId: number;
    quantity: number;
    id?: number;
  }> => {
    const unresolvedLabels: string[] = [];
    const mapped = productItems.map((item) => {
      const productId = resolveProductIdFromItem(item);
      if (productId == null) {
        const label =
          (item.productCode != null && String(item.productCode).trim() !== ''
            ? String(item.productCode).trim()
            : null) ??
          (item.productName != null && String(item.productName).trim() !== ''
            ? String(item.productName).trim()
            : null) ??
          'desconocido';
        unresolvedLabels.push(label);
        return null;
      }

      const result: { productId: number; quantity: number; id?: number } = {
        productId,
        quantity: parseInt(item.quantity, 10),
      };

      if (item.originalItemId) {
        result.id = item.originalItemId;
      }

      return result;
    });

    if (unresolvedLabels.length > 0) {
      throw new Error(
        `No se pudo resolver el ID de producto para: ${unresolvedLabels.join(', ')}. Seleccione el producto desde el catálogo.`,
      );
    }

    const productsForInventory = mapped.filter(
      (item): item is { productId: number; quantity: number; id?: number } => item != null,
    );

    if (productsForInventory.length === 0) {
      throw new Error('No valid products selected');
    }

    return productsForInventory;
  };

  /**
   * Al editar, añade entradas { id, delete: true } para cada línea de detalle
   * que existía al cargar y ya no figura en el payload.
   */
  const buildItemsWithDeletions = (lineItems: any[]): any[] => {
    if (!editMode || !recordToEdit) {
      return lineItems;
    }
    const kept = new Set<number>();
    for (const row of lineItems) {
      const iid = row?.id;
      if (iid != null && Number(iid) > 0) {
        kept.add(Number(iid));
      }
    }
    const out = [...lineItems];
    for (const oldId of editLoadedRecordItemIdsRef.current) {
      if (!kept.has(oldId)) {
        out.push({ id: oldId, delete: true });
      }
    }
    editLoadedRecordItemIdsRef.current = kept;
    return out;
  };

  // Show confirmation modal with message
  const startConfirmation = () => {
    setConfirmMessage(
      `¿Estás seguro de que deseas ${editMode ? 'actualizar' : 'confirmar'} este registro?`,
    );
  };

  // Execute the confirmation action (single-flight: no duplicate COMPLETED records)
  const executeConfirmation = async () => {
    if (confirmingRef.current || isConfirming || isSavingDraft) {
      return;
    }

    // Make sure we have a user ID, otherwise show an error
    if (!currentUser || !currentUser.id) {
      console.error('No user is authenticated');
      setConfirmMessage('No hay usuario autenticado. Inicie sesión de nuevo.');
      return;
    }

    // Validate supplier is selected
    if (!selectedSupplierId || selectedSupplierId === '') {
      console.error('No supplier selected');
      setConfirmMessage('Error: Debe seleccionar un proveedor para continuar.');
      return;
    }

    const supplierIdNum = parseInt(selectedSupplierId, 10);
    if (isNaN(supplierIdNum) || supplierIdNum <= 0) {
      console.error('Invalid supplier ID');
      setConfirmMessage('Error: Debe seleccionar un proveedor válido para continuar.');
      return;
    }

    const destinationLocationId = parseInt(selectedLocationId, 10);
    if (isNaN(destinationLocationId) || destinationLocationId <= 0) {
      console.error('No valid destination warehouse selected');
      setConfirmMessage('Error: Debe seleccionar un almacén válido para continuar.');
      return;
    }

    confirmingRef.current = true;
    setIsConfirming(true);

    try {
      const unitsForSave = buildUnitsPayloadForItems();
      // Check if we have individual unit data (alineado con filas del paso 1)
      if (unitsForSave.length > 0) {
        // Use the new endpoint with individual units
        // For updates, don't include recordCode (backend doesn't allow it)
        const baseItems = transformUnitsToBackendFormat(unitsForSave);
        const inventoryData: CreateInventoryRecordWithItemsDto | any = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'COMPLETED' as any,
          items: buildItemsWithDeletions(baseItems),
        };

        // IMPORTANT FIX: Only use the Redux mutation if onSave is NOT provided
        if (!onSave) {
          if (editMode && recordToEdit) {
            // Use update endpoint for edits
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              inventoryData,
            );
          } else {
            // Use create endpoint for new records
            await createInventoryRecordWithItems(inventoryData).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      } else {
        // Fallback to old method if no individual units
        const inventoryData = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'COMPLETED' as const,
        };

        // Map product items to the format expected by the API
        const productsForInventory = mapProductItemsForSave();

        const itemsForSave = buildItemsWithDeletions(productsForInventory);
        const payloadNoUnits = {
          ...inventoryData,
          items: itemsForSave,
        };

        if (!onSave) {
          if (editMode && recordToEdit) {
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              payloadNoUnits as any,
            );
          } else {
            await createInventoryRecord(payloadNoUnits).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            items: itemsForSave,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      }

      // Close only after successful save
      setShowConfirmModal(false);
      setConfirmMessage('');
      onClose();
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'create'} inventory record:`, error);
      setConfirmMessage(
        `No se pudo ${editMode ? 'actualizar' : 'crear'} el registro. Intente de nuevo.`,
      );
    } finally {
      confirmingRef.current = false;
      setIsConfirming(false);
    }
  };

  const resetConfirmation = () => {
    if (confirmingRef.current || isConfirming) {
      return;
    }
    setShowConfirmModal(false);
    setConfirmMessage('');
  };

  // Handle close click (show cancel confirmation)
  const handleCloseClick = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowCancelConfirmation(true);
  }, [isConfirming, isSavingDraft]);

  // Cancel operation (close panel)
  const cancelOperation = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose, isConfirming, isSavingDraft]);

  // Open save draft confirmation modal (called when user clicks "Guardar Borrador")
  const handleOpenSaveDraftConfirm = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    if (currentStep === 1) {
      if (!validateStep1()) {
        const hasSupplier = Boolean(selectedSupplierId && selectedSupplierId !== '');
        if (!hasSupplier) {
          setShowSupplierValidation(true);
        }
        setSnackbarMessage('Complete el paso 1: productos, cantidades y proveedor.');
        setSnackbarOpen(true);
        return;
      }
      if (productUnits.length > 0 && isReductionVersusSavedUnits(productItems, productUnits)) {
        reductionAfterActionRef.current = 'saveDraft';
        setShowReductionWarningModal(true);
        return;
      }
    }
    setShowSaveDraftConfirmModal(true);
  }, [
    currentStep,
    validateStep1,
    productItems,
    productUnits,
    selectedSupplierId,
    isConfirming,
    isSavingDraft,
  ]);

  // Handle save draft (saves with PENDING status - called after user confirms in modal)
  const handleSaveDraft = async () => {
    // Make sure we have a user ID
    if (!currentUser || !currentUser.id) {
      console.error('No user is authenticated');
      setSnackbarMessage('No hay usuario autenticado. Inicie sesión de nuevo.');
      setSnackbarOpen(true);
      return;
    }

    // Validate supplier is selected
    if (!selectedSupplierId || selectedSupplierId === '') {
      console.error('No supplier selected');
      setSnackbarMessage('Error: Debe seleccionar un proveedor para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    const supplierIdNum = parseInt(selectedSupplierId, 10);
    if (isNaN(supplierIdNum) || supplierIdNum <= 0) {
      console.error('Invalid supplier ID');
      setSnackbarMessage('Error: Debe seleccionar un proveedor válido para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    const destinationLocationId = parseInt(selectedLocationId, 10);
    if (isNaN(destinationLocationId) || destinationLocationId <= 0) {
      console.error('No valid destination warehouse selected');
      setSnackbarMessage('Error: Debe seleccionar un almacén válido para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    try {
      const unitsForDraft = buildUnitsPayloadForItems();
      // Check if we have individual unit data (alineado con filas del paso 1)
      if (unitsForDraft.length > 0) {
        // Use the new endpoint with individual units
        // For updates, don't include recordCode (backend doesn't allow it)
        const baseItemsDraft = transformUnitsToBackendFormat(unitsForDraft);
        const inventoryData: CreateInventoryRecordWithItemsDto | any = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'PENDING' as any,
          items: buildItemsWithDeletions(baseItemsDraft),
        };

        // IMPORTANT FIX: Only use the Redux mutation if onSave is NOT provided
        if (!onSave) {
          if (editMode && recordToEdit) {
            // Use update endpoint for edits
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              inventoryData,
            );
          } else {
            // Use create endpoint for new records
            await createInventoryRecordWithItems(inventoryData).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      } else {
        // Fallback to old method if no individual units
        // supplierId is required in parent handleSaveRecord → createWithItems / updateWithItems
        // or the record is stored without a supplier and will not show on re-edit
        const inventoryData = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'PENDING' as const, // Save as PENDING
        };

        // Map product items to the format expected by the API
        const productsForInventory = mapProductItemsForSave();

        const itemsForSaveDraft = buildItemsWithDeletions(productsForInventory);
        const payloadDraftNoUnits = {
          ...inventoryData,
          items: itemsForSaveDraft,
        };

        if (!onSave) {
          if (editMode && recordToEdit) {
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              payloadDraftNoUnits as any,
            );
          } else {
            await createInventoryRecord(payloadDraftNoUnits).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            items: itemsForSaveDraft,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      }

      setShowSaveDraftConfirmModal(false);
      onClose();
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'save'} draft:`, error);
      setSnackbarMessage(
        `No se pudo ${editMode ? 'actualizar' : 'guardar'} el borrador. Intente de nuevo.`,
      );
      setSnackbarOpen(true);
      throw error;
    }
  };

  // User confirmed save draft in modal - keep modal open with loading until save finishes
  const handleConfirmSaveDraft = async () => {
    if (savingDraftRef.current || isSavingDraft || isConfirming) {
      return;
    }
    savingDraftRef.current = true;
    setIsSavingDraft(true);
    try {
      await handleSaveDraft();
    } catch {
      // Error snackbar already set in handleSaveDraft; keep draft modal open for retry
    } finally {
      savingDraftRef.current = false;
      setIsSavingDraft(false);
    }
  };

  const handleCancelSaveDraftConfirm = useCallback(() => {
    if (savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowSaveDraftConfirmModal(false);
  }, [isSavingDraft]);

  // Handler for branch/location change
  const handleLocationChange = useCallback((value: string) => {
    // eslint-disable-next-line no-console
    console.log('Location changed to:', value);
    setSelectedLocationId(value);
  }, []);

  return {
    // State
    currentUser,
    locationsData,
    locationsLoading,
    locationsError,
    productsData,
    productsLoading,
    productsError,
    currentStep,
    productItems,
    selectedLocationId,
    selectedSupplierId,
    showSupplierValidation,
    handleSupplierChange,
    showConfirmModal,
    confirmMessage,
    showSaveDraftConfirmModal,
    snackbarOpen,
    snackbarMessage,
    setSnackbarOpen,
    productUnits,
    isSubmitting,
    isSubmittingWithItems,
    isConfirming,
    isSavingDraft,
    isSuccess,
    submitError,
    submitErrorWithItems,

    // Actions
    addProductRow,
    removeProductRow,
    updateProductItem,
    handleNextStep,
    handlePreviousStep,
    validateStep1,
    validateStep2,
    handleUpdateProperties,
    handleUpdateItem,
    startConfirmation,
    executeConfirmation,
    resetConfirmation,
    handleLocationChange,
    resetForm,
    handleOpenSaveDraftConfirm,
    handleConfirmSaveDraft,
    handleCancelSaveDraftConfirm,

    // Close confirmation
    showCancelConfirmation,
    setShowCancelConfirmation,
    handleCloseClick,
    cancelOperation,

    // Reducción de unidades: aviso antes del paso 2
    showReductionWarningModal,
    handleConfirmReductionWarning,
    handleCancelReductionWarning,
  };
};
